import { useEffect, useState } from "react";
import { IconChartBar } from "@tabler/icons-react";
import type { Organization } from "../utils/types";

interface DsoSettingsSubTabProps {
  organization: Organization | undefined;
  onSave: (daysSales: number, period: number) => void;
  isSaving: boolean;
}

const MIN_VALUE = 1;
const MAX_VALUE = 365;

function parsePositiveInt(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < MIN_VALUE || parsed > MAX_VALUE) {
    return null;
  }
  return parsed;
}

export function DsoSettingsSubTab({
  organization,
  onSave,
  isSaving,
}: DsoSettingsSubTabProps) {
  const [daysSalesInput, setDaysSalesInput] = useState("60");
  const [periodInput, setPeriodInput] = useState("45");

  useEffect(() => {
    if (!organization) {
      return;
    }
    setDaysSalesInput(String(organization.dso_days_sales ?? 60));
    setPeriodInput(String(organization.dso_period_days ?? 45));
  }, [organization?.dso_days_sales, organization?.dso_period_days]);

  const daysSales = parsePositiveInt(daysSalesInput);
  const period = parsePositiveInt(periodInput);
  const isValid = daysSales !== null && period !== null;
  const unchanged =
    organization != null &&
    daysSales === organization.dso_days_sales &&
    period === organization.dso_period_days;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || daysSales === null || period === null) {
      return;
    }
    onSave(daysSales, period);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-xl bg-linear-to-br from-emerald-500 to-teal-500 p-2 text-white shadow-md">
          <IconChartBar size={24} stroke={2} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Analytics</h2>
          <p className="text-sm text-gray-600">
            Configure how Days Sales Outstanding (DSO) is calculated
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-xl border border-emerald-100 bg-linear-to-br from-emerald-50/50 to-teal-50/30 p-6"
      >
        <p className="mb-6 text-sm text-gray-600">
          DSO = (Accounts Receivable ÷ Credit Sales) × Period. Credit sales
          include invoices created in the days-sales window ending on the
          as-of date.
        </p>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label
              htmlFor="dso-days-sales"
              className="mb-2 block text-sm font-semibold text-gray-700"
            >
              Days sales
            </label>
            <input
              id="dso-days-sales"
              type="number"
              min={MIN_VALUE}
              max={MAX_VALUE}
              value={daysSalesInput}
              onChange={(event) => setDaysSalesInput(event.target.value)}
              disabled={!organization || isSaving}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lookback window for credit sales (1–365 days)
            </p>
          </div>

          <div>
            <label
              htmlFor="dso-period"
              className="mb-2 block text-sm font-semibold text-gray-700"
            >
              Period
            </label>
            <input
              id="dso-period"
              type="number"
              min={MIN_VALUE}
              max={MAX_VALUE}
              value={periodInput}
              onChange={(event) => setPeriodInput(event.target.value)}
              disabled={!organization || isSaving}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-gray-500">
              Multiplier applied to the AR ÷ credit sales ratio (1–365 days)
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={!organization || !isValid || unchanged || isSaving}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-shadow hover:bg-emerald-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
